<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import { buildSeoMeta } from '@/utils/seo'
import { useAuthStore } from '@/stores/authStore'
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import TFNav from './components/tf/TFNav.vue'
import CookieConsentBanner from './components/common/CookieConsentBanner.vue'
import router from './router'
import { initAnalyticsIfConsented } from './utils/gtag'

const route = useRoute()
const authStore = useAuthStore()

// Accepting from the banner starts GA immediately, without a reload —
// initAnalyticsIfConsented re-checks the just-saved consent state itself.
function onConsentDecided() {
  initAnalyticsIfConsented(router)
}

// Detect if current route is a TF route (admin only, excluding CRM)
const isTFRoute = computed(() => {
  const isAdmin = authStore.isAdmin
  const isCRM = route.path.startsWith('/crm')

  return isAdmin && !isCRM
})

// Use centralized SEO helper for default meta tags
useHead(
  buildSeoMeta({
    route,
    // Default values - individual pages can override these
    type: 'website',
    lang: 'en',
  }),
)
</script>

<template>
  <div id="app">
    <!-- Header Component -->
    <Header />

    <!-- App Container with TF Nav -->
    <div class="app-container">
      <TFNav v-if="isTFRoute" />

      <!-- Main Content -->
      <main class="app-main" :class="{ 'with-tf-nav': isTFRoute }">
        <router-view />
      </main>
    </div>

    <!-- Footer -->
    <Footer />

    <CookieConsentBanner @decided="onConsentDecided" />
  </div>
</template>

<style>
/* Global styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  line-height: 1.6;
  color: #333;
  background-color: white;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

a {
  color: #555;
}
</style>
<style lang="scss" scoped>
/* App container with TF nav */
.app-container {
  display: flex;
  flex: 1;
  position: relative;
}

/* Main content */
.app-main {
  flex: 1;
  min-height: calc(100vh - 140px);
  margin: auto;
  width: 100%;
  max-width: 1600px;
  padding: 0 1.5rem;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  &.with-tf-nav {
    @media (min-width: 768px) and (max-width: 1700px) {
      width: calc(100% - 65px);
      margin-left: 65px;
    }
  }
  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
}
</style>
